import React, { useState } from 'react'
import { useLocation } from 'react-router-dom'

import styled from 'styled-components'
import moment, { Moment } from 'moment'

import KeyboardAccessible from 'components/KeyboardAccessible'
import Tooltip from 'components/Tooltip'

import { useSessionContext } from 'hooks/SessionContext'

import Apptentive from 'helpers/apptentive'

import Api from 'services/api'

import { color } from 'styles/colors'

type Props = {
  isPinned: boolean
  displayUnpinned: boolean
  customerName: string
  customerId: string
  pinnedAt?: Date | Moment | null
  className?: string
}

const Pin = ({ isPinned, displayUnpinned, customerName, customerId, pinnedAt = moment(), className = '' }: Props) => {
  const { session: { dealerId } } = useSessionContext()
  const location = useLocation()

  const [isDisplayedAsPinned, setIsDisplayedAsPinned] = useState(isPinned)
  const [displayedPinnedAt, setDisplayedPinnedAt] = useState(pinnedAt)

  const tooltipTitle = isDisplayedAsPinned
    ? `Pinned on ${moment(displayedPinnedAt).format('M/DD/YYYY')} at ${moment(displayedPinnedAt).format('h:mm A')}`
    : 'Pin this customer to the top of the customer list'

  const onClick = () => {
    if ((isDisplayedAsPinned && confirm(`Are you sure you want to unpin ${customerName}?`)) || !isDisplayedAsPinned) {
      Api.DiagnosticsCustomers.update(dealerId, customerId, { pinned: !isDisplayedAsPinned })
        .then((res) => {
          if (!isDisplayedAsPinned) {
            const eventName = location.pathname.includes('/dashboard') ? 'pin_customer_from_dashboard' : 'pin_customer_from_customers'
            Apptentive.sendEvent(eventName)
          }

          setIsDisplayedAsPinned(!isDisplayedAsPinned)
          setDisplayedPinnedAt(res.pinnedAt)
        })
    }
  }

  return (
    <Tooltip title={tooltipTitle} placement="top">
      <S.Pin
        {...{ onClick, displayUnpinned }}
        isPinned={isDisplayedAsPinned}
        aria-label={`${isDisplayedAsPinned ? 'unpin' : 'pin'} ${customerName}`}
      >
        <i
          className={`${className} icon-pin`}
          role="img"
        />
      </S.Pin>
    </Tooltip>
  )
}

export default Pin

const iconStyle = (isPinned: boolean, displayUnpinned: boolean) => {
  if (isPinned) {
    return `
      color: ${color('customerPin')};
    `
  } else if (displayUnpinned) {
    return `
      color: ${color('customerPinUnpinned')};
      transform: rotate(-25deg);
    `
  } else {
    return 'color: transparent;'
  }
}

const S = {
  Pin: styled(KeyboardAccessible).withConfig({
    shouldForwardProp: (prop) => !['displayUnpinned', 'isPinned'].includes(prop)
  })<{ isPinned: boolean, displayUnpinned: boolean }>`
    i {
      font-size: 15px;
      cursor: pointer;
      transition: all .2s ease;
      display: block;

      ${({ isPinned, displayUnpinned }) => iconStyle(isPinned, displayUnpinned)}
    }

    &:focus {
      i {
        ${({ isPinned }) => iconStyle(isPinned, true)}
      }
    }
  `
}
